import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WebApp from "@twa-dev/sdk";
import { authenticateUser } from "../../services/api/auth";
import { fetchProfiles, fetchNatalChart } from "../../services/api/profile";
import { useAuthStore } from "../../store/authStore";
import { useNatalChartStore } from "../../store/natalChartStore";
import { useProfileStore } from "../../store/profileStore";
import { Player } from "@lottiefiles/react-lottie-player";
import StepError from "../../components/common/StepError";
import { getUserFriendlyError } from "../../utils/errorMessages";
import styles from "./LoadingPage.module.css";
import type { Profile } from "../../store/profileStore";

const LoadingPage: React.FC = () => {
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const setTgId = useAuthStore((state) => state.setTgId);
  const setNatalChart = useNatalChartStore((state) => state.setNatalChart);
  const setProfile = useProfileStore((state) => state.setProfile);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isDev = process.env.NODE_ENV !== "production";

  const initDataErrorMessage = isDev
    ? "initData пустое"
    : "Не удалось запустить приложение. Попробуйте снова.";

  useEffect(() => {
    const waitForForecast = async () => {
      const MAX_TRIES = 10;
      const DELAY = 2000;

      for (let i = 0; i < MAX_TRIES; i++) {
        try {
          const profiles = await fetchProfiles();
          const main = profiles.find((p: Profile) => p.is_main);

          if (main?.forecast === 1) {
            setProfile(main);
            return true;
          }
        } catch (e) {
          console.warn("⚠️ Ошибка при получении профиля:", e);
        }

        await new Promise((res) => setTimeout(res, DELAY));
      }

      console.warn("⛔ Forecast не готов после всех попыток");
      return false;
    };

    const checkUser = async () => {
      try {
        if (!window.Telegram?.WebApp) throw new Error("Telegram WebApp SDK не доступен");

        const initData = WebApp.initData;
        if (!initData) throw new Error(initDataErrorMessage);

        const response = await authenticateUser(initData);
        setToken(response.token);
        setTgId(response.tg_id);

        if (response.onboarding) {
          const ready = await waitForForecast();

          if (!ready) throw new Error("Прогноз ещё не готов");

          const chart = await fetchNatalChart();

          if (!chart?.natalChart?.summary) {
            throw new Error("Не удалось получить натальную карту");
          }

          setNatalChart(chart.natalChart);
          navigate("/natal-chart");
        } else {
          navigate("/onboarding");
        }
      } catch (error: unknown) {
        console.error("❌ Ошибка авторизации или загрузки:", error);
        setErrorMessage(getUserFriendlyError(error));
      }
    };

    setTimeout(checkUser, 3000); // задержка для показа анимации
  }, [navigate, setToken, setTgId, setNatalChart, setProfile, initDataErrorMessage]);

  return (
    <div className={styles.container}>
      {errorMessage ? (
        <StepError message={errorMessage} onRetry={() => window.location.reload()} />
      ) : (
        <Player
          autoplay
          loop
          src="/animations/forecast-loader.json"
          style={{ height: "200px", width: "200px" }}
        />
      )}
    </div>
  );
};

export default LoadingPage;
